import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import ManpowerFormDialog from "./ManpowerFormDialog";
import { SiteFilterSelect } from "./shared/SiteFilterSelect";
import { NoAccess } from "../equipment/shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HO_ROLES = ["Director", "Manager", "Back Office"];

export default function ManpowerLogTab() {
  const { user } = useAuth();
  const isSE = user?.role === "Site Engineer";
  const isHO = HO_ROLES.includes(user?.role);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [siteLabel, setSiteLabel] = useState("");
  const [formTarget, setFormTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    setLoading(true);
    if (isSE) {
      client
        .get("/manpower-log/my", { params: { page: pageNum, page_size: 50 } })
        .then((res) => {
          setEntries(res.data.items);
          setSiteLabel(res.data.site);
          setTotalPages(res.data.total_pages);
          setPage(res.data.page);
        })
        .catch((err) => toast.error(getErrorMessage(err)))
        .finally(() => setLoading(false));
    } else if (isHO) {
      const params = { page: pageNum, page_size: 50 };
      if (siteFilter) params.site = siteFilter;
      if (dateFilter) params.date = dateFilter;
      client
        .get("/manpower-log", { params })
        .then((res) => {
          setEntries(res.data.items);
          setTotalPages(res.data.total_pages);
          setPage(res.data.page);
        })
        .catch((err) => toast.error(getErrorMessage(err)))
        .finally(() => setLoading(false));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(1), [siteFilter, dateFilter]);

  useEffect(() => {
    if (isHO) client.get("/manpower-log/sites").then((res) => setSites(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHO]);

  const openExisting = async (id) => {
    try {
      const res = await client.get(`/manpower-log/${id}`);
      setFormTarget(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!isSE && !isHO) return <NoAccess />;

  return (
    <div className="space-y-4" data-testid="manpower-log-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {isHO && (
            <>
              <SiteFilterSelect sites={sites} value={siteFilter} onChange={setSiteFilter} testId="manpower-site-filter" placeholder="All sites" allowAll />
              <Input type="date" data-testid="manpower-date-filter" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-44" />
            </>
          )}
          {isSE && siteLabel && (
            <p className="text-sm text-slate-500">
              Site: <span className="font-medium text-slate-700">{siteLabel}</span>
            </p>
          )}
        </div>
        {isSE && (
          <Button data-testid="manpower-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Manpower Log
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="manpower-table">
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              {isHO && <TableHead>Site</TableHead>}
              <TableHead>Company Staff</TableHead>
              <TableHead>Contract Labour</TableHead>
              <TableHead>Total</TableHead>
              {isSE && <TableHead>Editable</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                  No entries found.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((e) => (
                <TableRow
                  key={e.id}
                  data-testid={`manpower-row-${e.id}`}
                  className={`cursor-pointer ${isSE && e.locked ? "opacity-60" : ""}`}
                  onClick={() => setFormTarget(e)}
                >
                  <TableCell>{new Date(e.date).toLocaleDateString("en-IN")}</TableCell>
                  {isHO && <TableCell className="text-xs">{e.site_display_label || e.site}</TableCell>}
                  <TableCell className="text-xs">
                    {(e.company_staff || []).length} row{(e.company_staff || []).length === 1 ? "" : "s"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {(e.contract_labour || []).length} row{(e.contract_labour || []).length === 1 ? "" : "s"}
                  </TableCell>
                  <TableCell className="font-mono font-semibold">{e.total_manpower}</TableCell>
                  {isSE && <TableCell>{e.locked ? <Badge className="bg-slate-400">Locked</Badge> : <Badge className="bg-emerald-600">Editable</Badge>}</TableCell>}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="manpower-pagination" />

      {formTarget && (
        <ManpowerFormDialog
          entry={formTarget === "new" ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onOpenExisting={openExisting}
          onSaved={() => {
            setFormTarget(null);
            load(page);
          }}
        />
      )}
    </div>
  );
}
